/**
 * spreadsheetCauseRanking.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Deterministic cause ranker for the Spreadsheet Uploader domain. Runs in the
 * frontend so that Wingman receives a pre-ranked, module-appropriate decision
 * payload instead of relying on the LLM to sort explanations loosely.
 *
 * The backend has a mirror of this logic as a safety fallback — see
 * `/app/backend/routes/wingman.py` (`_rank_spreadsheet_causes`). Keep the two
 * in step when adding or reweighting causes.
 *
 * Public API:
 *   rankSpreadsheetCauses(ssot) → {
 *     rankedCauses: [{ cause, label, score, why }, ...],
 *     rankedChecks: [string, ...],   // priority-ordered
 *   }
 *
 * Causes (fixed enum, spec-defined):
 *   - biomass_count_mismatch
 *   - true_efficiency_problem
 *   - feeding_inconsistency
 *   - environment_or_health
 *   - input_validation_required
 */

const CAUSE_META = {
  biomass_count_mismatch: {
    label: 'Biomass / count mismatch',
    checks: [
      'Verify current fish count via physical count, sonar scan, or harvest-so-far tally',
      'Cross-check sample batch sizes against mortality logs for this period',
      'Confirm the stocking number used by the model matches the record on the cage chart',
    ],
  },
  true_efficiency_problem: {
    label: 'True efficiency problem (feed → growth)',
    checks: [
      'Check water temperature logs against the model assumption for this period',
      'Audit feed type / pellet size / brand changes inside the window',
      'Confirm there were no feeding interruptions or quality issues',
    ],
  },
  feeding_inconsistency: {
    label: 'Feeding inconsistency (overfeeding / underfeeding / mixed)',
    checks: [
      'Audit feed machine calibration and daily feed log entries',
      'Check for uneaten feed / pellet waste observed on harvest or sampling days',
      'Review whether feeding was paused due to weather, spawning, or disease',
    ],
  },
  environment_or_health: {
    label: 'Environment or health event',
    checks: [
      'Review the mortality pattern and cumulative mortality rate for the period',
      'Check recent temperature and dissolved-oxygen excursions',
      'Inspect fish for disease signs / recent treatments',
    ],
  },
  input_validation_required: {
    label: 'Input validation required (data quality)',
    checks: [
      'Re-verify the flagged samples / feed entries — a data-entry error can invalidate everything below it',
      'Check that sample batch sizes and dates match the original cage record',
      'Confirm no zero-value or placeholder rows slipped into the upload',
    ],
  },
};

// Safe numeric read.
function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

/**
 * Main ranker. Input is the `ssot` object (or the window bridge copy) from
 * generateIntelligence.js. Pure function — no side effects, no network.
 * Always returns exactly the 5 enum causes scored; the caller can slice the
 * top N.
 */
export function rankSpreadsheetCauses(ssot = {}) {
  const confidence = ssot.confidenceLabel || null;
  const hasMissingSignal = !!ssot.hasMissingFishSignal
    || (!!ssot.missingFishRangeLabel && ssot.missingFishRangeLabel !== 'Within margin');
  const rfi = num(ssot.rfi);
  const wastePct = num(ssot.wasteSharePct);
  const feedingState = ssot.feedingState || null;
  const hasConflict = !!ssot.hasConflict;
  const hasSuspectedError = !!ssot.hasSuspectedError;
  const outlierCount = num(ssot.fcrOutliers?.count) || 0;
  const strongConfidence = confidence === 'High' || confidence === 'Moderate';
  const weakConfidence = confidence === 'Low'
    || confidence === 'Indication Only'
    || confidence === 'Uncertain';

  const scores = {
    input_validation_required: 0,
    biomass_count_mismatch: 0,
    feeding_inconsistency: 0,
    true_efficiency_problem: 0,
    environment_or_health: 0,
  };
  const notes = {
    input_validation_required: [],
    biomass_count_mismatch: [],
    feeding_inconsistency: [],
    true_efficiency_problem: [],
    environment_or_health: [],
  };

  // ─── input_validation_required ────────────────────────────────────────
  // Data-quality faults always surface first — a bad row invalidates every
  // downstream interpretation until it is corrected.
  if (hasSuspectedError) {
    scores.input_validation_required += 90;
    notes.input_validation_required.push('hasSuspectedError flag raised on the record');
  }
  if (hasConflict) {
    scores.input_validation_required += 70;
    notes.input_validation_required.push('conflicting FCR / growth signals');
  }
  if (outlierCount > 0) {
    scores.input_validation_required += 50;
    notes.input_validation_required.push(`${outlierCount} extreme-FCR period(s) excluded`);
  }

  // ─── biomass_count_mismatch ───────────────────────────────────────────
  if (hasMissingSignal && strongConfidence) {
    scores.biomass_count_mismatch += 60;
    notes.biomass_count_mismatch.push(`missing-fish signal with ${confidence} confidence`);
  } else if (hasMissingSignal && weakConfidence) {
    scores.biomass_count_mismatch += 30;
    notes.biomass_count_mismatch.push(`missing-fish signal at ${confidence} confidence — indicative only`);
  }

  // ─── feeding_inconsistency ────────────────────────────────────────────
  if (feedingState === 'Mixed') {
    scores.feeding_inconsistency += 45;
    notes.feeding_inconsistency.push('feeding state is Mixed — periods of both over- and under-feeding');
  } else if (feedingState === 'Overfeeding' || feedingState === 'Underfeeding') {
    scores.feeding_inconsistency += 30;
    notes.feeding_inconsistency.push(`feeding state: ${feedingState}`);
  }
  if (wastePct !== null && wastePct >= 5) {
    scores.feeding_inconsistency += 35;
    notes.feeding_inconsistency.push(`waste share ${wastePct.toFixed(1)}% ≥ 5% economic threshold`);
  }

  // ─── true_efficiency_problem ──────────────────────────────────────────
  // Underperforming RFI without a missing-fish signal points at real
  // efficiency loss rather than a stocking error.
  if (!hasMissingSignal && rfi !== null && rfi < 85) {
    scores.true_efficiency_problem += 50;
    notes.true_efficiency_problem.push(`RFI ${rfi}% below the 85% underperformance threshold`);
  }
  // Ambiguous case: weak missing-fish signal at low confidence could also
  // be a true efficiency problem in disguise. Give it a moderate score.
  if (hasMissingSignal && weakConfidence) {
    scores.true_efficiency_problem += 35;
    notes.true_efficiency_problem.push('weak missing-fish signal — could equally be efficiency loss');
  }

  // ─── environment_or_health ────────────────────────────────────────────
  // Catch-all. Base score plus a bump when we have no clear mechanistic
  // explanation from the other buckets.
  scores.environment_or_health += 20;
  const othersTotal = scores.biomass_count_mismatch
    + scores.feeding_inconsistency
    + scores.true_efficiency_problem
    + scores.input_validation_required;
  if (othersTotal < 40) {
    scores.environment_or_health += 25;
    notes.environment_or_health.push('no strong mechanistic signal elsewhere — consider environment/health');
  }
  if (!strongConfidence) {
    scores.environment_or_health += 10;
    notes.environment_or_health.push(`${confidence || 'Uncertain'} confidence leaves room for unmodelled factors`);
  }

  // ─── Assemble sorted output ───────────────────────────────────────────
  const rankedCauses = Object.keys(scores)
    .map((cause) => ({
      cause,
      label: CAUSE_META[cause].label,
      score: scores[cause],
      why: notes[cause].join('; ') || null,
    }))
    .sort((a, b) => b.score - a.score);

  // Priority-ordered checks come from the #1 cause. Fall back to the #2 cause
  // if the top cause is environment_or_health (catch-all) so we return
  // something actionable.
  const topCause = rankedCauses[0].cause === 'environment_or_health'
    ? (rankedCauses[1] || rankedCauses[0]).cause
    : rankedCauses[0].cause;
  const rankedChecks = CAUSE_META[topCause].checks.slice(0, 3);

  return { rankedCauses, rankedChecks };
}
